import React, {useMemo} from 'react'
import {useToggle} from '@startlibs/core'
import {Errors, Field, TextInput, validation, WithForm} from '@startlibs/form'
import {bearerHeader, TEXT_HTML} from '../reducers'
import {confirmPasswords, getErrorForField} from '../lib/validation'
import {SplitColumnsContainer} from '@startlibs/components'
import {Card, CardActionButton, CardHeader, FieldRequirements, SignInLayout} from '../components/SigninLayout'
import {PasswordInput} from '../components/PasswordInput'
import {jwtDecode} from 'jwt-decode'
import {JwtLinkExpiredError} from '../errors/LinkExpiredError'
import {FormattedMessage, useIntl} from "react-intl";
import {postFetcher} from "@startlibs/utils";


export const allRequired = (intl) => (s, k) => !s && intl.formatMessage({
  defaultMessage: 'All fields are required.',
  description: 'Validation error message for required fields'
})

const TextInputMaybeLocked = ({value, form, path, ...rest}) =>
  value
    ? <TextInput
      {...rest}
      value={value}
      disabled
      locked
    />
    : <TextInput
      {...rest}
      tabIndex={1}
      path={path}
    />

export const JwtRegistrationForm = ({userToggle, expired, system, jwt}) => {
  const success = useToggle()
  const loading = useToggle()
  const visiblePassword = useToggle()
  const expiration = useToggle()
  const intl = useIntl()

  const onFailure = validation.responseFailure((response) => {
    if (response.response.status === 401) {
      expiration.open()
      return true
    }
    const {title, type, message, fieldErrors} = response
    return ((message === 'error.validation' || message === 'error.http.401') && Array.isArray(fieldErrors) && fieldErrors.reduce((acc, {field}) => ({...acc, ...getErrorForField(intl)(field)}), {})) ||
        (title === 'passwordCannotBeEqualToThePreviousPasswords' && {password: [intl.formatMessage({
              defaultMessage: `Your new password must be different from your previous {pwdInHistory} passwords.`,
              description: 'JWT Password cannot be equal to the previous ones'
            },
            {
              pwdInHistory: system.systemAuthPolicy.pwdInHistory
            }
          )]}) ||
        (type === 'https://pas.purview.net/problem/invalid-password' && {password: []}) ||
        (message === 'error.emailexists' && {email: [intl.formatMessage({
            defaultMessage: 'This email is already registered',
            description: 'Registration error message for already registered email'
          })]})
  })

  const onSuccess = (_, {id_token}) => {
    loading.open()
    window.location = system.systemWebHooks.systemAuthWebHook + id_token + "&locale="+intl.locale
  }

  const parsedJwt = useMemo(() => jwtDecode(jwt),[jwt])


  const preValidation = validation.buildValidation({
    password: [allRequired(intl), confirmPasswords(intl,'newPassword')]
  })
  const {sub: email} = parsedJwt
  if (expired || expiration.isOpen) {
    const loginInfo = JSON.parse(parsedJwt.loginInfo)
    return <JwtLinkExpiredError system={system} signinUrl={loginInfo.url} />
  }

  return <SignInLayout system={system}>
    <Card>
      <CardHeader>
        <h1><FormattedMessage
          defaultMessage="Password registration"
          description="JWT Registration page title"
        /></h1>
        <p><FormattedMessage
          defaultMessage="Please create a password to complete your account."
          description="JWT Registration page instructions"
        /></p>
      </CardHeader>
      <WithForm
        alwaysSave
        preValidation={preValidation}
        onSuccess={onSuccess}
        onFailure={onFailure}
        action={({password}) => postFetcher('/pasapi/register-token', password,bearerHeader(jwt,TEXT_HTML))}
      >{ form => <>
        <TextInput
          label={intl.formatMessage({
            defaultMessage: "Email",
            description: "JWT Registration, email field label"
          })}
          raw
          value={email}
          locked
          disabled
        />
        <SplitColumnsContainer>
          <Field label={intl.formatMessage({
            defaultMessage: "Create password",
            description: "JWT Registration, password field label"
          })}>
            <PasswordInput
              tabIndex={1}
              name="password"
              path="password"
              syncVisible={visiblePassword}
              withoutField
            />
          </Field>
          <Field label={intl.formatMessage({
            defaultMessage: "Confirm new password",
            description: "JWT Registration, confirm password field label"
          })}>
            <PasswordInput
              tabIndex={1}
              onPaste={(e) => e.preventDefault()}
              path="newPassword"
              name="newPassword"
              syncVisible={visiblePassword}
              withoutField
            />
          </Field>
        </SplitColumnsContainer>
        <FieldRequirements hasErrors={form.hasErrors('password')}>
          <FormattedMessage
            defaultMessage="Your new password must be <b>{pwdMinimumLength} or more characters</b>, containing at least <b>one uppercase letter</b>, one <b>number</b> and one <b>special character</b>"
            description="Password requirements"
            values={{
              pwdMinimumLength: system.systemAuthPolicy.pwdMinimumLength,
              b: msg => <b>{msg}</b>
            }}
          />
        </FieldRequirements>
        <Errors />
        <CardActionButton
          isLoading={form.isLoading || loading.isOpen}
          className="highlight with-loader"
          tabIndex={1}
          type="submit"
          highlight
        >
          <FormattedMessage
            defaultMessage="Register password"
            description="JWT Registration, submit button"
          />
        </CardActionButton>
      </>}</WithForm>
    </Card>
  </SignInLayout>
}
